import { axisFuelInputsFromPlans } from '../../../components/NowBar/axisFuelInputs';
import { fetchPilotStoredFuel } from '../../FuelPlans/pilotStoredFuel';
import { type LocalDB } from '../../LocalDB/LocalDB';
import {
    FlightSupplementalDataType,
    type FlightSupplementalFuelPlan,
    type SupplementalFuelPlan,
} from '../FlightSupplementalDataTypes';
import { type UpcomingReservationEvent } from './useUpcomingReservations';

/**
 * (#3029) Palivové vstupy z osy do LOKÁLNÍ databáze — aby pilot o čísla nepřišel bez signálu.
 *
 * Osa sama se nesynchronizuje (je to výstup přepočtu celé flotily, ne data letu) a ani nemusí:
 * pilotnímu řetězu stačí dvě hodnoty na leg. Ukládá se proto jen projekce, jeden záznam na
 * letadlo, a veze se na téže synchronizaci jako rezervace a letiště (`updateSupplementalData`
 * běží po každém syncu pod `whenOnline`).
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Kolik dní za posledním legem si záznam ještě necháme, než ho uklidí `cleanupSupplementalData`. */
const USEFUL_FOR_DAYS_AFTER_LAST_LEG = 2;

export function getSupplementalFuelPlanDbId(aircraftId: string): string {
    return `fuel-${aircraftId}`;
}

/** Okno osy: od včerejška (poslední skutečnost seeduje řetěz) po den za posledním legem. */
function windowFor(times: number[]): { fromIso: string; toIso: string } {
    const from = Math.floor((Date.now() - DAY_MS) / DAY_MS) * DAY_MS;
    const to = Math.ceil((Math.max(...times) + DAY_MS) / DAY_MS) * DAY_MS;

    return { fromIso: new Date(from).toISOString(), toIso: new Date(to).toISOString() };
}

export async function loadFuelPlans(db: LocalDB, upcomingEvents: UpcomingReservationEvent[]): Promise<void> {
    const byAircraftId = new Map<string, UpcomingReservationEvent[]>();
    for (const event of upcomingEvents) {
        if (event.aircraftId) {
            byAircraftId.set(event.aircraftId, [...(byAircraftId.get(event.aircraftId) ?? []), event]);
        }
    }
    const records: FlightSupplementalFuelPlan[] = [];

    for (const [aircraftId, events] of byAircraftId.entries()) {
        const times = events.map((event) => event.event.dateTo.getTime()).filter((time) => Number.isFinite(time));
        if (times.length === 0) {
            continue;
        }
        try {
            const stored = await fetchPilotStoredFuel({ aircraftId, ...windowFor(times) });
            const inputs = axisFuelInputsFromPlans(events, stored.plans);
            if (
                Object.keys(inputs.upliftByEventId).length === 0 &&
                Object.keys(inputs.seedByReservationId).length === 0 &&
                Object.keys(inputs.fobByEventId ?? {}).length === 0
            ) {
                // Úspěšná odpověď BEZ jediného plánu (osa letadlo ještě nespočítala, okno mimo
                // její dosah) se neukládá: přepsat platnou kopii prázdnem by pilota poslalo na
                // zamrzlá pole rezervace — tedy na uplift 0 — pod hlavičkou o čerstvém přepočtu.
                continue;
            }
            const fuelPlan: SupplementalFuelPlan = { aircraftId, computedAt: stored.computedAt, ...inputs };
            records.push({
                id: getSupplementalFuelPlanDbId(aircraftId),
                type: FlightSupplementalDataType.fuelPlan,
                usefulnessEndsByDate: new Date(Math.max(...times) + USEFUL_FOR_DAYS_AFTER_LAST_LEG * DAY_MS),
                fuelPlan,
            });
        } catch (error) {
            // Nedostupná osa nesmí shodit zbytek synchronizace — pilot pak jede na tom, co má
            // v databázi z minula (nebo bez podstrčení, na hodnotách z rezervace).
            console.error(`Fuel plans for ${aircraftId} could not be loaded`);
            console.error(error);
        }
    }

    if (records.length > 0) {
        await db.flightSupplementalData.bulkPut(records);
    }
}
